import React from 'react';
import { XMarkIcon } from './icons';

interface ChangelogModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ChangelogModal: React.FC<ChangelogModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-slate-900">更新履歴</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* コンテンツ */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          <div className="space-y-8">
            {/* Version 1.8.0 */}
            <div className="border-l-4 border-teal-600 pl-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-teal-100 text-teal-800">
                  v1.8.0
                </span>
                <span className="text-sm text-slate-500">2026年8月21日</span>
              </div>
              
              <h3 className="text-lg font-semibold text-slate-900 mb-3">ショーケース・ギャラリーの高速表示とスタイル引き継ぎ改善</h3>
              
              <div className="space-y-4 text-slate-700">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">表示・パフォーマンス</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>ショーケースをキャッシュ先出し＋ページング対応にし、読み込みを高速化</li>
                    <li>ショーケース／ギャラリーの読み込み中に作品カード形のスケルトン表示を追加</li>
                    <li>ギャラリー・「最近の生成」もキャッシュ優先で、空表示や初回のちらつきを抑制</li>
                    <li>認証直後の空レスポンスで既存キャッシュを上書きしないよう改善</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">スタイル引き継ぎ</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>「このスタイルで」押下時にポートレート等のプロンプトが入力欄へ復元されるよう修正</li>
                    <li>ショーケース追加時に userPrompt など自由記述設定を保存するよう改善</li>
                    <li>引き継ぎ時に Firestore から全文を再取得し、キャッシュ短縮による欠落を防止</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">導線・その他</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>ショーケース左上のロゴ／ブランド名クリックでアプリに戻れるように変更</li>
                    <li>ショーケース追加のタイムアウト対策（REST 書き込み優先・楽観的 UI）</li>
                    <li>ショーケースから戻ったあとにクレジット表示が 0 になる問題を改善</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Version 1.7.0 */}
            <div className="border-l-4 border-slate-300 pl-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                  v1.7.0
                </span>
                <span className="text-sm text-slate-500">2026年8月</span>
              </div>
              
              <h3 className="text-lg font-semibold text-slate-900 mb-3">ポートレートモードとショーケースからのスタイル引き継ぎ</h3>
              
              <div className="space-y-4 text-slate-700">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">新機能</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>ポートレートモード（/portrait）を追加。写真添付＋フリープロンプト入力のプロ向け生成</li>
                    <li>本人性保持用の固定プロンプトを自動付与（服・背景・ポーズは参照写真からコピーしない）</li>
                    <li>ショーケース各作品に「このスタイルで」マークを追加し、設定を引き継いで自分の写真で再生成可能に</li>
                    <li>ショーケース追加時に再生成用の詳細 settings を保存するよう改善</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">導線・UI</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>ログイン後ホームと通常モード結果画面に「ポートレートモード」ボタンを追加</li>
                    <li>/portrait のヘッダーを「Profile Studio for Portrait」に変更</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Version 1.6.0 */}
            <div className="border-l-4 border-slate-300 pl-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                  v1.6.0
                </span>
                <span className="text-sm text-slate-500">2026年1月</span>
              </div>
              
              <h3 className="text-lg font-semibold text-slate-900 mb-3">🎻 音楽家向けページと決済・クレジット周りの改善</h3>
              
              <div className="space-y-4 text-slate-700">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">🎻 新機能</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>音楽家向けページ（/musician）を追加</li>
                    <li>背景・衣装・袖・楽器・顔の向きなど演奏家向けスタイルをカスタマイズ可能</li>
                    <li>Before/After 画像で「私服の自撮り→演奏会向け宣材写真」を表示</li>
                    <li>ログイン後のホームに「音楽家版を作る」ボタンを追加</li>
                    <li>/musician のヘッダーを「Profile Studio for Musician」に変更</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">💳 決済・クレジット</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>決済完了後に「ホームに戻る」を押してもログイン状態が維持されるように改善</li>
                    <li>Stripe 決済時にクレジットを加算するように修正（複数回購入で残高が加算）</li>
                    <li>残りクレジットの表示を Firestore の creditsRest に合わせて正しく減るように修正</li>
                    <li>新規登録時（Google・メール）に無料枠 creditsRest: 3 を Firestore に保存</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">🐛 バグ修正・改善</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>Webhook で userId を metadata と client_reference_id の両方で渡すように強化</li>
                    <li>PaymentSuccess / PaymentCancel で SPA 内遷移を使用して認証状態を維持</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Version 1.5.0 */}
            <div className="border-l-4 border-slate-300 pl-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                  v1.5.0
                </span>
                <span className="text-sm text-slate-500">2025年1月19日</span>
              </div>
              
              <h3 className="text-lg font-semibold text-slate-900 mb-3">✨ ユーザー体験の向上とデザイン改善</h3>
              
              <div className="space-y-4 text-slate-700">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">📊 新機能</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>画像生成中の進行状況表示機能（プログレスバーとステップ表示）</li>
                    <li>4秒ごとに進行状況が更新され、現在の処理段階を表示</li>
                    <li>風船の背景テンプレートを追加（無料プラン）</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">🎨 デザイン改善</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>ウォーターマークをカメラアイコンの中に「P」を配置したデザインに変更</li>
                    <li>ヘッダーのロゴマークと同じフォルムのカメラアイコンを使用</li>
                    <li>透過度30%で右下にさりげなく表示</li>
                    <li>ウォーターマークのサイズを2倍に拡大</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Version 1.4.0 */}
            <div className="border-l-4 border-slate-300 pl-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                  v1.4.0
                </span>
                <span className="text-sm text-slate-500">2025年10月17日</span>
              </div>
              
              <h3 className="text-lg font-semibold text-slate-900 mb-3">🌟 ショーケース機能とギャラリーUIの大幅改善</h3>
              
              <div className="space-y-4 text-slate-700">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">🎨 新機能</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>作品を公開ギャラリー（ショーケース）に追加できる機能</li>
                    <li>ショーケースからの削除機能（ホウキアイコンで片付け）</li>
                    <li>独立したshowcase.htmlページを作成（ログイン不要で閲覧可能）</li>
                    <li>背景・衣装・ポーズ情報を正確に表示</li>
                    <li>ギャラリー上部にボタン説明を追加</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">🎭 無料プラン機能拡張</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>無料プランで選択可能な背景を追加（白の木目、美容室、カフェ、コンサートホール、ビーチ、都会の街並み）</li>
                    <li>有料プラン限定オプションを視覚的に表示（薄いグレー）</li>
                    <li>カスタム背景・衣装は有料プラン限定</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">💧 ウォーターマーク機能</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>無料プランの画像に自動的にウォーターマークを追加</li>
                    <li>ダウンロード時にリアルタイムでウォーターマークを適用</li>
                    <li>長押しダウンロードにもウォーターマーク対応</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">🐛 バグ修正</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>Firebase初期化のデバッグログを追加</li>
                    <li>クレジット管理をcreditsRestのみに統一</li>
                    <li>画像生成後の自動リダイレクト問題を修正</li>
                    <li>無料ユーザーの画像がギャラリーに保存されない問題を修正</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">🔒 セキュリティ強化</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>Firestoreルールを追加（showcaseコレクションの読み書き権限）</li>
                    <li>所有者のみショーケースから削除可能</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Version 1.3.0 */}
            <div className="border-l-4 border-slate-300 pl-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                  v1.3.0
                </span>
                <span className="text-sm text-slate-500">2025年10月9日</span>
              </div>
              
              <h3 className="text-lg font-semibold text-slate-900 mb-3">🎉 サブスクリプション管理機能を追加</h3>
              
              <div className="space-y-4 text-slate-700">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">🔐 新機能</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>Pro Maxプラン契約者向けのサブスクリプション管理機能</li>
                    <li>Stripe Customer Portalで解約・決済方法変更が可能に</li>
                    <li>請求履歴と領収書のダウンロード機能</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">👔 衣装ラインナップ更新</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>ビジネススーツ（ライトグレー）を追加</li>
                    <li>アロハシャツを追加</li>
                    <li>カジュアルシャツ（ホワイト）を追加</li>
                    <li>全衣装に画像プレビューを実装</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">🐛 バグ修正</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>カスタム画像選択後の状態管理を改善</li>
                    <li>衣装・背景変更時に前回の画像が残る問題を修正</li>
                    <li>正面ポーズのプロンプト表示を明確化</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">💳 本番環境対応</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>Stripe本番環境への完全移行</li>
                    <li>Node.js 20 & Firebase Functions V2への移行</li>
                    <li>環境変数管理の改善</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">📝 その他の改善</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>プロンプト品質の向上（無料版・有料版統一）</li>
                    <li>利用規約の更新（プロフィールスタジオに統一）</li>
                    <li>セキュリティ情報の詳細化</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Version 1.2.0 */}
            <div className="border-l-4 border-slate-300 pl-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                  v1.2.0
                </span>
                <span className="text-sm text-slate-500">2025年10月7日</span>
              </div>
              
              <h3 className="text-lg font-semibold text-slate-900 mb-3">初期リリース</h3>
              
              <div className="space-y-2 text-slate-700">
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  <li>AI宣材写真生成機能</li>
                  <li>複数の背景・衣装テンプレート</li>
                  <li>無料体験（3枚）機能</li>
                  <li>Stripe決済統合</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* フッター */}
        <div className="border-t p-4 bg-slate-50 text-center">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};

